import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { magicLinkApi } from '../services/api';
import { Loader2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import MfaChallengeStep from '../components/auth/MfaChallengeStep';
import BusyBeeLogo from '../components/BusyBeeLogo';
import { takeSignInReturn } from '../lib/signInReturn';

/**
 * Landing page for the URL embedded in magic-link emails: /auth/magic?token=...
 *
 * Calls backend to consume the token. Resolves to either a session JWT
 * (happy path) or an MFA challenge (the user enabled 2FA between request and
 * click). Renders the appropriate step.
 */
const MagicLinkReturnPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { finalizeSession } = useAuth();
  const [error, setError] = useState('');
  const [challenge, setChallenge] = useState(null);
  // React.StrictMode in dev mounts twice; the consume endpoint is single-use,
  // so the second mount would always fail. Guard against it.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const token = searchParams.get('token');
    if (!token) {
      setError(t('auth.magic.invalidLink'));
      return;
    }

    (async () => {
      try {
        const { data } = await magicLinkApi.consume(token);
        if (data.mfa_required) {
          setChallenge(data.challenge_token);
          return;
        }
        await finalizeSession(data.access_token);
        navigate(takeSignInReturn(), { replace: true });
      } catch (err) {
        setError(err.response?.data?.detail || t('auth.errors.generic'));
      }
    })();
  }, [searchParams, navigate, finalizeSession, t]);

  const handleMfaSuccess = async (accessToken) => {
    await finalizeSession(accessToken);
    navigate(takeSignInReturn(), { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <BusyBeeLogo className="h-10" />
        </div>
        {challenge ? (
          <MfaChallengeStep
            challengeToken={challenge}
            onSuccess={handleMfaSuccess}
            onCancel={() => navigate('/login')}
          />
        ) : error ? (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
            <div>
              <p className="text-red-400 text-sm font-medium">{error}</p>
              <button
                onClick={() => navigate('/login')}
                className="text-amber-400 text-sm hover:text-amber-300 mt-3 underline"
              >
                {t('auth.backToLogin')}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 justify-center text-gray-300">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{t('auth.magic.signingIn')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MagicLinkReturnPage;
